import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import dayjs from 'dayjs';

export type BackgroundTaskStatus = 'pending' | 'running' | 'done' | 'error';

export interface BackgroundTask {
  id: string;
  title: string;
  subtitle: string;
  status: BackgroundTaskStatus;
  progressCurrent: number;
  progressTotal: number;
  errorMessage?: string;
  startedAt: Date;
}

export interface BackgroundTaskDefinition<T> {
  title: string;
  subtitle?: string;
  run: (onProgress: (current: number, total: number) => void) => Observable<T>;
  onComplete?: (result: T) => void;
  onError?: (err: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class BackgroundTaskService {
  private readonly tasksSubject = new BehaviorSubject<BackgroundTask[]>([]);

  readonly tasks$ = this.tasksSubject.asObservable();

  readonly activeTasks$ = this.tasks$.pipe(
    map((tasks) => tasks.filter((t) => t.status === 'pending' || t.status === 'running'))
  );

  private readonly subscriptions = new Map<string, Subscription>();
  private readonly cancelSignals = new Map<string, Subject<void>>();

  addTask<T>(definition: BackgroundTaskDefinition<T>): string {
    const id = `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const cancelSignal$ = new Subject<void>();

    const task: BackgroundTask = {
      id,
      title: definition.title,
      subtitle: definition.subtitle ?? dayjs().format('YYYY-MM-DD HH:mm'),
      status: 'pending',
      progressCurrent: 0,
      progressTotal: 0,
      startedAt: new Date(),
    };

    this.tasksSubject.next([...this.tasksSubject.value, task]);
    this.cancelSignals.set(id, cancelSignal$);
    this.updateTask(id, { status: 'running' });

    const onProgress = (current: number, total: number): void => {
      this.updateTask(id, { progressCurrent: current, progressTotal: total });
    };

    const sub = definition
      .run(onProgress)
      .pipe(takeUntil(cancelSignal$))
      .subscribe({
        next: (result) => {
          this.updateTask(id, { status: 'done', progressCurrent: task.progressTotal });
          definition.onComplete?.(result);
          this.scheduleRemoval(id, 4000);
        },
        error: (err: unknown) => {
          const msg =
            err != null && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'An unexpected error occurred';
          this.updateTask(id, { status: 'error', errorMessage: msg });
          definition.onError?.(err);
          this.scheduleRemoval(id, 8000);
        },
        complete: () => {
          this.cleanupTask(id);
        },
      });

    this.subscriptions.set(id, sub);
    return id;
  }

  cancelTask(id: string): void {
    this.cancelSignals.get(id)?.next();
    this.cancelSignals.get(id)?.complete();
    this.cancelSignals.delete(id);
    this.subscriptions.get(id)?.unsubscribe();
    this.subscriptions.delete(id);
    this.removeTask(id);
  }

  dismissTask(id: string): void {
    this.removeTask(id);
  }

  private updateTask(id: string, patch: Partial<BackgroundTask>): void {
    const updated = this.tasksSubject.value.map((t) =>
      t.id === id ? { ...t, ...patch } : t
    );
    this.tasksSubject.next(updated);
  }

  private removeTask(id: string): void {
    this.tasksSubject.next(this.tasksSubject.value.filter((t) => t.id !== id));
  }

  private cleanupTask(id: string): void {
    this.cancelSignals.delete(id);
    this.subscriptions.delete(id);
  }

  private scheduleRemoval(id: string, delay: number): void {
    this.cleanupTask(id);
    setTimeout(() => this.removeTask(id), delay);
  }
}